import {
  SubtaskPayload,
  TaskTemplatePayload,
  TaskTemplate as APITaskTemplate,
  TaskInstance as APITaskInstance,
  TaskInstancePayload,
} from '@sdk/shiftTasks/types/apiTypes';
import { Attachment as APIAttachment } from '@sdk/attachments/types';
import { PersonaCollection } from '@sdk/personaCollection/types/PersonaCollection';
import { isUser } from '@sdk/users/utils/guards';

import { escapeAttachmentUrl } from '@web/common/utils/escapeAttachmentUrl';
import { TaskTemplateFormValues } from '@web/shiftTasks/ComposeTask/types/TaskTemplateFormValues';
import { TaskTemplate } from '@web/shiftTasks/types/TaskTemplate';
import { Attachment } from '@web/shiftTasks/types/Attachment';
import { TaskInstance } from '@web/shiftTasks/types/TaskInstance';
import { TaskStatuses } from '@web/shiftTasks/types/TaskStatuses';
import {
  SubtaskInstanceFormValues,
  TaskInstanceFormValues,
} from '@web/shiftTasks/ComposeTask/types/TaskInstanceFormValues';

function mapAttachment(
  attachment: Attachment,
  shouldCopyAttachments?: boolean
): Partial<APIAttachment> {
  if (attachment.id && !shouldCopyAttachments) {
    return attachment;
  }

  // new attachments should have no id but the picker adds `id:0`
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, ...attachmentWithoutId } = attachment;
  return attachmentWithoutId;
}

/* ---- Templates ---- */
export const getTaskTemplatePayload = (
  formValues: TaskTemplateFormValues,
  shouldCopyAttachments?: boolean
): TaskTemplatePayload => {
  const { attachments, title, description, subtasks } = formValues;

  return {
    attachments: attachments.map((attachment) => mapAttachment(attachment, shouldCopyAttachments)),
    description,
    subtasks: mapTemplateSubtasks(subtasks),
    title,
  };
};

function mapTemplateSubtasks(subtasks: { isDeleted?: boolean; title: string }[]) {
  return subtasks
    .filter((s) => !s.isDeleted)
    .map((s) => s.title)
    .filter(Boolean);
}

export function mapTaskTemplateAPIToUI(taskTemplate: APITaskTemplate): TaskTemplate {
  const { id, subtasks, attachments, created, title, description } = taskTemplate;

  return {
    id,
    subtasks,
    attachments,
    createdAt: new Date(created),
    title,
    description,
  };
}

export function mapTaskTemplateToFormValues(taskTemplate: TaskTemplate): TaskTemplateFormValues {
  const { attachments = [], subtasks, title = '', description = '' } = taskTemplate;
  const escapedAttachments = escapeAttachmentUrl(attachments);

  return {
    attachments: escapedAttachments,
    description,
    subtasks: subtasks?.map((title) => ({ title })) || [],
    title,
  };
}

/* ---- Instances ---- */
export function mapTaskInstanceToFormValues(instance: TaskInstance): TaskInstanceFormValues {
  const { attachments = [], subtasks, title = '', status, description = '' } = instance;

  return {
    attachments,
    description,
    subtasks: subtasks?.map(({ title, id, status }) => ({ title, id, status })) || [],
    status,
    title,
  };
}

function mapInstanceSubtasks(subtasks: SubtaskInstanceFormValues[]): SubtaskPayload[] {
  return subtasks
    .filter(({ title, id }) => id || title)
    .map((subtask) => {
      const { id, title, status, isDeleted } = subtask;
      if (!id) {
        return { title, status: TaskStatuses.PENDING };
      }

      return {
        id,
        title,
        deleted: isDeleted,
        status,
      };
    });
}

export function getTaskInstancePayload(
  id: ID,
  formValues: TaskInstanceFormValues
): TaskInstancePayload {
  const { attachments, description, subtasks, status, title } = formValues;

  return {
    attachments: attachments.map((attachment) => mapAttachment(attachment)),
    description,
    id,
    subtasks: mapInstanceSubtasks(subtasks),
    status,
    title,
  };
}

export function mapTaskInstanceAPIToUI(
  taskInstance: APITaskInstance,
  personaCollection: PersonaCollection
): TaskInstance {
  const {
    assigneeId,
    attachments,
    created,
    description,
    dtend,
    dtstart,
    id,
    openEnd,
    status,
    subtasks,
    title,
  } = taskInstance;

  const assignee = assigneeId ? personaCollection.personasDict[assigneeId] : null;
  const instanceAssignee =
    assignee && isUser(assignee)
      ? {
          id: assignee.id,
          fullname: assignee.fullname,
        }
      : null;

  return {
    assignee: instanceAssignee,
    attachments,
    createdAt: new Date(created),
    dtend: new Date(dtend),
    dtstart: new Date(dtstart),
    description,
    id,
    isOpenEnd: openEnd,
    subtasks,
    status,
    title,
  };
}
